import SearchBar from "../../components/SearchBar";
import MainContainer from "../../layout/MainContainer";
import { useEffect, useState } from "react";
import { Button, Checkbox } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import FilterMenu from "../../components/filterMenu";
import { isValidInput } from "../../utils/validations";
import {
  useAddMemberToGroupMutation,
  useLazyAllMembersByGroupQuery,
  useLazyAllMembersQuery,
} from "../../service/user";
import { showError, showToast } from "../../constants/toasts";
import dayjs from "dayjs";

const AddMember = () => {
  const navigate = useNavigate();
  const [getUsers] = useLazyAllMembersQuery();
  const [groupMembers] = useLazyAllMembersByGroupQuery();
  const { id } = useParams();
  const [addMember] = useAddMemberToGroupMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [members, setMembers] = useState([]);

  const handleCheckboxChange = (userId: string) => {
    setSelectedUserIds(
      (prevSelected) =>
        prevSelected?.includes(userId)
          ? prevSelected?.filter((id) => id !== userId) // Remove if already selected
          : [...prevSelected, userId] // Add if not selected
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.includes("all")) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(["all"]);
    }
  };

  const isAllSelected = selectedUserIds.includes("all");

  const addMemberApi = async () => {
    if (!selectedUserIds?.length) {
      showError("Please select a member to add");
      return;
    }

    setLoading(true);
    const body = {
      groupId: id,
      memberIds: selectedUserIds,
    };

    try {
      const response = await addMember(body).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        showToast(response?.message);
        navigate(-1);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getGroupMembers = async () => {
    setLoading(true);
    try {
      const response = await groupMembers({
        search: debouncedSearchTerm.trim(),
        id,
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setMembers(response?.data || []);
        prefillSelection(users, response?.data);
      } else {
        setMembers([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const prefillSelection = (userList: any[], groupMemberList: any[]) => {
    const memberIds = groupMemberList?.map((m) => m?.userId);
    const userIds = userList.map((u) => u?.userId);

    const allMatched = userIds?.every((id) => memberIds?.includes(id));

    if (allMatched && userIds?.length > 0) {
      setSelectedUserIds(["all"]);
    } else {
      setSelectedUserIds(memberIds);
    }
  };

  const getUsersApi = async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setUsers(response?.data || []);
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getUsersApi();
    getGroupMembers();
  }, [debouncedSearchTerm]);

  return (
    <>
      <MainContainer>
        <div className="main_title d_flex">
          <div className="left_s">
            <h1>Select Members</h1>
          </div>
          <div className="right_s">
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search...."
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <FilterMenu
              startDate={null}
              endDate={null}
              setStartDate={() => { }}
              setEndDate={() => { }}
            />

            <Button className="btn btn_primary" onClick={() => addMemberApi()}>
              Add
            </Button>

            <Button
              onClick={() => navigate(`/manage-cms/viewGroup/${id}`)}
              className="btn btn_primary"
            >
              Back
            </Button>
          </div>
        </div>
        <div className="cards table_cards">
          <div className="table_container">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead
                style={{
                  backgroundColor: "#C19857",
                  color: "#fff",
                  textAlign: "left",
                }}
              >
                <tr>
                  <th>
                    <Checkbox
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Membership Type</th>
                  <th>Membership Status</th>
                  <th>Payment Status</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {users?.length
                  ? users?.map((row: any, index) => (
                    <tr key={index}>
                      <td>
                        <Checkbox
                          checked={
                            isAllSelected ||
                            selectedUserIds.includes(row?.userId)
                          }
                          onChange={() => handleCheckboxChange(row?.userId)}
                        />
                      </td>
                      <td>
                        <div className="user_block click_action">
                          <figure>
                            <img
                              src={"/static/images/user_placeholder.png"}
                              alt={row?.name || "-"}
                            />
                          </figure>
                          <p>{row.name}</p>{" "}
                        </div>
                      </td>
                      <td>{row?.email || "-"}</td>
                      <td>{row?.membershipType || "-"}</td>
                      <td>{row?.membership || "-"}</td>
                      <td>{row?.paymentStatus || "-"}</td>
                      <td>
                        {" "}
                        {dayjs(row?.createdAt).format(
                          "DD - MMMM - YYYY  |  hh:mm A"
                        )}
                      </td>
                    </tr>
                  ))
                  : null}
              </tbody>
            </table>
          </div>
        </div>
      </MainContainer>
    </>
  );
};
export default AddMember;
